import React, { useState, useEffect } from 'react';
import { extinguisherService, uploadService, geocodeService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { compressImage, formatFileSize, isCompressionSupported } from '../utils/imageCompression';

function ExtinguisherForm() {
  const [formData, setFormData] = useState({
    address: '',
    lat: null,
    lng: null,
    expiration: '',
    imageUrl: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [originalFileSize, setOriginalFileSize] = useState(0);
  const [compressedFileSize, setCompressedFileSize] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          try {
            const address = await geocodeService.reverseGeocode(latitude, longitude);
            setFormData(prev => ({
              ...prev,
              lat: latitude,
              lng: longitude,
              address: address
            }));
          } catch (error) {
            console.error('Geocoding failed:', error);
            setFormData(prev => ({
              ...prev,
              lat: latitude,
              lng: longitude
            }));
          }
        },
        (error) => {
          console.error('Geolocation failed:', error);
          setError('Unable to get your location. Please enter address manually.');
        }
      );
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Check if compression is supported
    if (!isCompressionSupported()) {
      setError('Image compression is not supported in this browser');
      return;
    }

    setError('');
    setCompressing(true);
    setOriginalFileSize(file.size);

    try {
      // Compress the image
      const compressedFile = await compressImage(file, 200); // 200KB max
      
      setSelectedFile(compressedFile);
      setCompressedFileSize(compressedFile.size);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(compressedFile);
      
      // Show success message if compression was significant
      const compressionRatio = ((file.size - compressedFile.size) / file.size * 100);
      if (compressionRatio > 10) {
        setMessage(`Image compressed from ${formatFileSize(file.size)} to ${formatFileSize(compressedFile.size)} (${compressionRatio.toFixed(1)}% reduction)`);
      }
      
    } catch (error) {
      console.error('Image compression failed:', error);
      setError('Failed to compress image. Please try a different image.');
    } finally {
      setCompressing(false);
    }
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;
    
    try {
      const { data } = await uploadService.getUploadUrl(
        selectedFile.name,
        selectedFile.type
      );
      
      const uploadResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }
      
      return data.imageUrl;
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      let imageUrl = formData.imageUrl;
      
      if (selectedFile) {
        imageUrl = await uploadImage();
      }

      await extinguisherService.create({
        ...formData,
        imageUrl,
        userId: user?.id
      });

      setMessage('Fire extinguisher reported successfully!');
      setFormData({
        address: formData.address,
        lat: formData.lat,
        lng: formData.lng,
        expiration: '',
        imageUrl: ''
      });
      setSelectedFile(null);
      setImagePreview('');
      setOriginalFileSize(0);
      setCompressedFileSize(0);
    } catch (error) {
      setError('Failed to report fire extinguisher. Please try again.');
      console.error('Submit error:', error);
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}
      
      <div className="form-group">
        <label htmlFor="address">Address</label>
        <input
          type="text"
          id="address"
          value={formData.address}
          onChange={(e) => setFormData({...formData, address: e.target.value})}
          required
          placeholder="Enter or wait for auto-fill..."
        />
      </div>

      <div className="form-group">
        <label htmlFor="expiration">Expiration Date</label>
        <input
          type="date"
          id="expiration"
          value={formData.expiration}
          onChange={(e) => setFormData({...formData, expiration: e.target.value})}
          required
          min={new Date().toISOString().split('T')[0]}
        />
      </div>

      <div className="form-group">
        <label>Image (Optional - automatically compressed to 200KB)</label>
        
        {/* File picker input */}
        <input
          type="file"
          id="image-file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={compressing}
          style={{ display: 'none' }}
        />
        
        {/* Camera input */}
        <input
          type="file"
          id="image-camera"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          disabled={compressing}
          style={{ display: 'none' }}
        />
        
        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => document.getElementById('image-file').click()}
            disabled={compressing}
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: compressing ? 'not-allowed' : 'pointer',
              opacity: compressing ? 0.6 : 1,
              fontSize: '0.9rem'
            }}
          >
            📁 Choose File
          </button>
          
          <button
            type="button"
            onClick={() => document.getElementById('image-camera').click()}
            disabled={compressing}
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: compressing ? 'not-allowed' : 'pointer',
              opacity: compressing ? 0.6 : 1,
              fontSize: '0.9rem'
            }}
          >
            📷 Take Photo
          </button>
        </div>
        
        {compressing && (
          <div className="compression-status">
            <p>🔄 Compressing image...</p>
          </div>
        )}
        
        {selectedFile && !compressing && (
          <div className="file-info">
            <p>✅ File ready: {formatFileSize(compressedFileSize)}</p>
            {originalFileSize > compressedFileSize && (
              <p className="compression-info">
                Original: {formatFileSize(originalFileSize)} → Compressed: {formatFileSize(compressedFileSize)}
              </p>
            )}
          </div>
        )}
        
        {imagePreview && (
          <div className="image-preview">
            <img src={imagePreview} alt="Preview" />
          </div>
        )}
      </div>

      <button type="submit" className="btn" disabled={loading || !formData.address}>
        {loading ? 'Submitting...' : 'Report Extinguisher'}
      </button>
    </form>
  );
}

export default ExtinguisherForm;